import React, { ReactNode } from "react";

export type DailySectionState =
    | "loading"
    | "ok"
    | "no_activity"
    | "insufficient"
    | "partial"
    | "stale"
    | "forbidden"
    | "error";

type Tone = "neutral" | "info" | "warning" | "critical";

type Copy = {
    title: string;
    body: string;
    tone: Tone;
};

// FR-024: every non-ok state has its own words. SC-023: `insufficient` is never
// dressed as `critical` — too little evidence is not the same as a failure.
const COPY: Record<Exclude<DailySectionState, "ok" | "partial">, Copy> = {
    loading: { title: "Cargando", body: "Leyendo la evidencia más reciente.", tone: "neutral" },
    no_activity: { title: "Sin actividad", body: "No hubo actividad en esta ventana. No es un error.", tone: "neutral" },
    insufficient: { title: "Evidencia insuficiente", body: "La muestra es demasiado pequeña para dar un valor.", tone: "info" },
    stale: { title: "Evidencia atrasada", body: "La evidencia más reciente es más antigua de lo que esta sección espera.", tone: "warning" },
    forbidden: { title: "No disponible para tu cuenta", body: "Tu cuenta no tiene acceso a esta sección.", tone: "info" },
    error: { title: "No se pudo cargar", body: "La fuente falló. Los valores que se muestran son los anteriores.", tone: "critical" },
};

// `partial` and `stale` still render their content: a degraded section must not
// blank out data that is on screen and honest about its age.
const SHOWS_CONTENT: Record<DailySectionState, boolean> = {
    loading: false,
    ok: true,
    no_activity: false,
    insufficient: false,
    partial: true,
    stale: true,
    forbidden: false,
    error: true,
};

type Props = {
    state: DailySectionState;
    label: string;
    detail?: string;
    onRetry?: () => void;
    children?: ReactNode;
};

export const DailyStateBlock: React.FC<Props> = ({ state, label, detail, onRetry, children }) => {
    // D113: a partial reading gets no page-level banner. Each figure carries its own
    // honesty (a dash plus its reason) where it is drawn.
    if (state === "ok" || state === "partial") return <>{children}</>;

    const copy = COPY[state];
    return (
        <>
            <div
                className={`daily-state daily-state--${copy.tone}`}
                data-state={state}
                role="status"
                aria-live="polite"
                aria-label={`${label}: ${copy.title}`}
            >
                {/* State is carried by text, not by colour alone. */}
                <p className="daily-state__title">{copy.title}</p>
                <p className="daily-state__body">{detail ?? copy.body}</p>
                {state === "error" && onRetry !== undefined ? (
                    <button type="button" className="daily-button daily-state__retry" onClick={onRetry}>
                        Reintentar: {label}
                    </button>
                ) : null}
            </div>
            {SHOWS_CONTENT[state] ? children : null}
        </>
    );
};
